import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Api, netflixToLibraryItem } from "../lib/api.js";
import { MediaStrip } from "../components/MediaStrip.js";

/**
 * Person page — IMDb-style filmography for one actor/creator, reached by
 * clicking any name in a cast strip. Everything listed plays through the
 * usual rd: pipeline.
 */
export function Person(): JSX.Element {
  const { name = "" } = useParams();
  const nav = useNavigate();
  const decoded = decodeURIComponent(name);

  const q = useQuery({
    queryKey: ["person", decoded],
    queryFn: () => Api.person.credits(decoded),
    staleTime: 6 * 60 * 60_000,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex gap-5">
          <div className="skeleton w-32 aspect-[2/3] rounded-lg" />
          <div className="flex-1 space-y-3 pt-2">
            <div className="skeleton h-6 w-64" />
            <div className="skeleton h-3 w-32" />
            <div className="skeleton h-3 w-full max-w-xl" />
            <div className="skeleton h-3 w-full max-w-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="card p-12 text-center">
        <p className="text-parchment font-display tracking-smallcaps">
          Couldn&rsquo;t find &ldquo;{decoded}&rdquo; on TMDB.
        </p>
        <Link to="/" className="pill-ghost inline-flex items-center gap-2 mt-6">
          <ArrowLeft size={14} /> Home
        </Link>
      </div>
    );
  }

  const { person, movies, series } = q.data;
  const bio = person.biography;
  const bioShort = bio && bio.length > 700 ? `${bio.slice(0, 700).replace(/\s+\S*$/, "")}…` : bio;

  return (
    <div className="space-y-8">
      <button onClick={() => nav(-1)} className="pill-ghost inline-flex items-center gap-2 text-sm">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex flex-col sm:flex-row gap-5">
        {person.photo_url ? (
          <img
            src={person.photo_url}
            alt={person.name}
            className="w-32 sm:w-40 aspect-[2/3] object-cover rounded-lg border border-hairline shrink-0"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-32 sm:w-40 aspect-[2/3] rounded-lg border border-hairline bg-elev flex items-center justify-center text-5xl font-display text-parchment/40 shrink-0">
            {person.name.slice(0, 1)}
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-3xl font-display text-ivory tracking-smallcaps">{person.name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-parchment/70">
            {person.known_for && <span className="section-label">{person.known_for}</span>}
            {person.birthday && <span>b. {person.birthday}</span>}
            {person.place_of_birth && <span>{person.place_of_birth}</span>}
          </div>
          {bioShort && (
            <p className="text-parchment/85 text-sm leading-relaxed mt-4 max-w-[75ch] whitespace-pre-wrap">
              {bioShort}
            </p>
          )}
        </div>
      </div>

      {movies.length > 0 && (
        <MediaStrip
          label="Movies"
          items={movies.map(netflixToLibraryItem)}
          variant="poster"
          caption={`${movies.length} films · stream via Real-Debrid`}
        />
      )}
      {series.length > 0 && (
        <MediaStrip
          label="TV shows"
          items={series.map(netflixToLibraryItem)}
          variant="poster"
          caption={`${series.length} shows · stream via Real-Debrid`}
        />
      )}
      {movies.length === 0 && series.length === 0 && (
        <p className="text-parchment/60 text-sm">No streamable credits found for {person.name}.</p>
      )}
    </div>
  );
}
